import React, { useRef } from "react";
import ScrollModal from "../ui/ScrollModal.jsx";
import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import BannerCTA from "../CTA/CTABanner.jsx";
import ppacca from './images/ppacca.png'
import smacca from './images/smarketsacca.gif'


import Tooltip from "./Tooltip";
import "./guides.css";

const AccaBetsGuide = () => {
    const scrollRef = useRef(null);

  const meta = guidesConfig.accaBetsGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo 
        title={meta.title} 
        description={meta.description} 
        url={guidesConfig.accaBetsGuide?.route || "/guides"} 
      />

      <p>
        Accumulator bets, or "accas," are popular among punters. They’re exciting, and offer higher odds.
      </p>
      <p>
        The bookies love them too. Frankly, they're a goldmine for the bookies, with plenty of profit margin built in for them. So they love to put on acca promos.
      </p>
      <p>And we, as matched bettors, love a promo as a profit opportunity!</p>

      <h2>What is an Accumulator Bet?</h2>
      <p>
        An accumulator bet combines several selections, or <Tooltip content="These are individual bets that are combined to form a multi-selection wager">legs</Tooltip>, into one single bet.
      </p>
      <h6 className="info">To win an acca, every leg must succeed; one losing leg means the entire accumulator loses.</h6>
      <div className="example-container">
        <h4>Example</h4>
        <p><strong>Jane places a £10 3-leg acca:</strong></p>
        <ul>
          <li>Liverpool to win (odds 1.5)</li>
          <li>Man United to win (odds 2.0)</li>
          <li>Chelsea to win (odds 1.8)</li>
        </ul>
        <p>
          Total Acca Odds = 1.5 x 2.0 x 1.8 = <strong>5.4</strong>
        </p>
        <p>If she wins, she'll get £54 back.</p>
      </div>

      <h2>Why Use Accas in Matched Betting?</h2>
      <p>
        Bookmakers often provide enticing promotions such as... 
      </p>
      <ul>
        <li><strong>Bet and Get</strong> offers</li>
        <li><strong>Acca Insurance</strong> (where you receive a refund as a free bet if one leg loses)</li>
        <li><strong>Boosted odds</strong></li>
      </ul>
      <p>These promotions give matched bettors the opportunity to lock in guaranteed profits (as well as offering <Tooltip content="Bets that you don't lay, but are worth more in potential profit than the odds suggest.">advantage play</Tooltip> opportunities, which are advanced strategies).</p>

      <h2>Two Ways to Lay an Accumulator...</h2>

      <h2>Method 1: Sequential Lay (Most Common)</h2>
      <p>
  <span className="key">Sequentially laying</span> means placing a separate lay bet on each leg individually—hedging your risk for each match after the last leg ends.
</p>
<h6 className="info">When we sequentially lay, we stop laying if the previous leg loses.</h6>
<h3>Reasons to choose sequential lay</h3>
<ul>
            <li><strong>Minimise Liability:</strong> We only lay one leg at once, so we don't have to have the maximum <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> tied up per bet over the whole acca, and you don't have tie any up if an earlier leg has lost.</li>
            <li><strong>Avoid Smarkets Eating Your Profit:</strong> Placing each lay bet individually bypasses the extra margins added in by Smarkets.</li>
        </ul>

<h3>Downsides of sequential lay</h3>
<ul>
  <li>Odds may drift (either way) between individual bets.</li>
  <li>More time consuming to lay every leg.</li>
  <li>Risk of human error, such as forgetting to place one of the necessary bets.</li>
  <li>Although there are calculators for this, the underlying maths is complex and hard to follow.</li>
</ul>
<h3>How to sequentially lay an acca</h3>
<div className="grid-fixed g2">
    <div className="col">
        <img src={ppacca} alt="Paddy Power acca" />
    </div>
    <div className="col">
    <ol>
        <li>
          <strong>Find Matches:</strong> Choose selections from matches scheduled at different times <strong>with no overlap</strong>, with close odds between the bookie and any exchange.
          <br />
          <div className="example-container">
        <h4>Example</h4>
          <ul>
            <li>Inter to win (odds 2.3, 5pm)</li>
            <li>Manchester United to win (odds 2.63, 8pm)</li>
            <li>Burnley to win (odds 1.5, tomorrow)</li>
          </ul>
      </div>
        </li>
        <li>
          <strong>Place Your Accumulator Bet:</strong> Ensure all promotion conditions, such as minimum odds and number of legs, are met.
        </li>
        </ol>
    </div>
</div>
          <BannerCTA 
        to="/guides/oddsmonkey-guide" 
        text="Read about how to find matches" 
        icon={ArrowUpRight} 
      />
      
        <ol start="3">
        <li>
          <strong>Lay Each Leg Individually:</strong> After placing your acca, use a sequential lay matched betting calculator to find the appropriate lay stake for each leg.
          <br />
          <div className="example-container">
        <h4>Example</h4>
          <ul>
            <li><span className="lay">Lay</span> Inter immediately, at odds 2.4.</li>
            <li>If Inter wins, <span className="lay">lay</span> Manchester United at the current odds of 2.8.</li>
            <li>If Manchester United wins, <span className="lay">lay</span> Burnley  at the current odds of 1.53.</li>          
        </ul>
      </div>
        </li>
      </ol>
      <h6 className="warn">Stop laying if any leg loses. The acca is complete.</h6>

      <h2>Method 2: Lock-in Profit with Smarkets</h2>
      <p>
  Locking in profit means placing one combined lay bet on the entire accumulator at once. You guarantee your profit, or <span className="key">qualifying loss</span>, no matter what happens.
</p>
<h6 className="info">Currently only Smarkets let you lay accas</h6>
<h3>Reasons to lock in</h3>

<ul>
  <li><strong>Simplified Process:</strong> Consolidates the entire lay bet into one transaction.</li>
  <li><strong>Legs can Overlap:</strong> It doesn't matter if events happen at the same time.</li>
  <li><strong>Guaranteed Profit:</strong> Ensures a secured profit, or <span className="key">qualifying loss</span>, even iff odds drift.</li>
  <li><strong>Easier to understand:</strong> No advanced calculator needed, can use standard matched betting lay calculators.</li>
</ul>

<h3>Downsides of locking in</h3>
<ul>
  <li>Smarkets build in their own profit margin, eating further into your profit.</li>
  <li>Your whole <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> is locked up until the bet settles.</li>
</ul>
<h3>How to lock in an acca</h3>

<h2>How to Lay an Acca on Smarkets</h2>

<h3>Step 1: Create the accumulator at the Bookmaker</h3>
<p><strong>Create the accumulator at the Bookmaker as before.</strong></p>
<h6 className="warn">Do not place the bet yet.</h6>

<h3>Step 2: Create the entire acca at Smarkets</h3>
<p>Select the <span className="back">back</span> odds for each outcome you want to use.</p>
<h6 className="warn">Do not select the lay odds, even if it feels counter-intuitive.</h6>
<p>At the bottom of the betslip in the <span className="key">multiples</span> section, switch from <span className="key">buy</span> to <span className="key">sell</span>. You have converted it to a lay bet.</p>

<h3>Step 3: Calculate Lay Stake</h3>
<p>Use the lay stake calculator to work out the right lay stake for the odds offered by Smarkets.</p>

<CTALarge 
  to="/calculators/lay-stake-calculator" 
  text="Go to the Lay Stake Calculator" 
  icon={Calculator} 
/>

<h3>Step 4: Enter your lay stake</h3>
<p>Check Smarkets will let you lay your desired amount.</p>

<h3>Step 5: Place your back bet</h3>
<p>Place your <span className="back">back</span> bet with the bookie.</p>

<h3>Step 6: Lay the acca</h3>
<p>Hit the <span className="key">SELL</span> button to <span className="lay">lay</span> the acca.</p>

      <img src={smacca} alt="How to lay an acca on Smarkets" />
      <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om3"}} />

      <h2>Advanced: Acca Insurance</h2>
      <p>We won't cover this advanced profit strategy in detail, but let's quickly talk about acca insurance offers.</p>
    <h6 className="info">Acca insurance offers typically refund your stake (usually as a free bet) if one leg of your accumulator lets you down.</h6>
    <p><strong>No-lay method:</strong> You place a normal acca and hope one leg loses while the others win. If that happens, you get a free bet, which you can convert to cash using standard matched betting. It’s not risk-free but can be profitable over time. This is a type of <Tooltip content="Bets that you don't lay, but are worth more in potential profit than the odds suggest.">advantage play</Tooltip>.
    </p>
    <p><strong>Lay method:</strong> You back each leg in the acca, then lay each one at a betting exchange. If all legs win, you lose slightly due to lay odds. But if one loses and the rest win, you get a free bet, covering the small losses.</p>
    <p>In both methods, the aim is to lose one leg while triggering the refund.</p>
      <ol>
        <li>
          <strong>Place the Accumulator at the Bookmaker:</strong> Ensure your bet fully meets the insurance promotion terms.
        </li>
        <li>
          <strong>No Lay at the Exchange:</strong> In this strategy, you do not lay the bet. If the acca wins outright, you profit directly. However, if one leg loses, you get a free bet which you can convert into profit using standard matched betting techniques.
        </li>
      </ol>

      <h2>Practical Tips for Acca Bets</h2>
      <ul>
        <li><strong>Match Timing:</strong> Ensure there's no overlap between matches when sequential laying.</li>
        <li><strong>Use Matched Betting Tools:</strong> Use <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> to save accas and calculate lay bets when sequential laying</li>
        <li><strong>Confirm Terms Carefully:</strong> Always check promotion requirements like minimum odds, number of legs, and eligible markets.</li>
        <li><strong>Monitor Your Bankroll:</strong> Make sure you have enough bankroll available at the exchange to cover your highest possible <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip>.</li>
      </ul>
    </div>
  );
};

export default AccaBetsGuide;
